import { buildHeaders, buildURL, handleApiError } from './commons/CommonService.Functions';
import { HttpErrorHandler } from '../functions/common';

// models
import {
  ProviderInboxAlreadyResolved,
  ProviderInboxIdentifyResult,
  ProviderInboxResolveRequest,
  ProviderInboxResolveResult,
} from '../models/Entities/ProviderInbox/ProviderInbox';
import { ErrorType } from '../models/HttpError';
import { IPagedResult } from '../models/HttpPagedResult';

/**
 * Bandeja de proveedores (spec 009, US4; MedicalProtocols 031 §4): leer una factura para identificar al emisor, asociarla
 * a un proveedor existente o nuevo y descartarla. El gateway mapea /protocols/{everything} → MedicalProtocols.
 * `identify` es multipart: usa `fetch` propio porque `buildHeaders` fuerza `Content-Type: application/json` (el navegador
 * pone el `boundary` del `FormData`). `resolve` también, para leer `data` del 409.
 */

const Gateway = process.env.REACT_APP_BILLER_API_GATEWAY;
const InboxURL = () => `${Gateway}protocols/providers/inbox`;

/** Error de la bandeja: ErrorType + `data` del 409 "ya resuelta". */
export interface ProviderInboxError extends ErrorType {
  AlreadyResolved?: ProviderInboxAlreadyResolved;
}

export const IdentifyURL = (): string => `${InboxURL()}/identify`;
export const ResolveURL = (inboxId: string): string => buildURL(InboxURL(), inboxId, 'resolve');
export const DiscardURL = (inboxId: string): string => buildURL(InboxURL(), inboxId);

/** `fetch` con el manejo de errores de red de los servicios (ErrorType con código 0). */
const send = async (url: string, init: RequestInit, context: string): Promise<Response> => {
  try {
    return await fetch(url, init);
  } catch (error: unknown) {
    throw await handleApiError(error, context);
  }
};

/** Respuesta no OK → ErrorType con el código HTTP y los `errors[]` (o el texto) del cuerpo. */
const failure = async (response: Response, context: string): Promise<{ error: ProviderInboxError; body: string }> => {
  console.error(`Error in ${context}`, response.status);
  const body = await response.text().catch(() => '');
  const error: ProviderInboxError = HttpErrorHandler(body || null, response);
  // El 413 del gateway puede venir sin cuerpo útil; el código manda.
  error.ErrorCode = response.status;
  return { error, body };
};

/** Envoltura `{ succeeded, message, errors, data }`: `succeeded: false` con 200 se trata como error. */
const dataOf = async <T>(response: Response): Promise<T | undefined> => {
  if (response.status === 204) return undefined;
  const text = await response.text().catch(() => '');
  if (!text) return undefined;
  const envelope = JSON.parse(text) as IPagedResult<T>;
  if (envelope && envelope.succeeded === false) {
    const error: ErrorType = { ErrorCode: response.status, ErrorMessage: envelope.message || 'Operation failed', Errors: envelope.errors ?? [] };
    throw error;
  }
  return envelope?.data ?? undefined;
};

/**
 * POST protocols/providers/inbox/identify (multipart, campo `file`; pdf, docx, xlsx, xls, csv hasta 50 MB) → lo que se leyó
 * y la acción propuesta. Si IA falla responde 200 con `NeedsInput` y el motivo en `warnings`. Errores: 400 tipo / tamaño,
 * 413 (también del gateway), 502 almacenamiento; red = ErrorCode 0.
 */
export const IdentifyProviderInvoice = async (token: string, file: File): Promise<ProviderInboxIdentifyResult> => {
  const context = 'IdentifyProviderInvoice';
  const form = new FormData();
  form.append('file', file, file.name);
  const response = await send(
    IdentifyURL(),
    { method: 'POST', body: form, headers: { Accept: 'application/json', Authorization: `Bearer ${token}` } },
    context
  );
  if (!response.ok) throw (await failure(response, context)).error;
  const data = await dataOf<ProviderInboxIdentifyResult>(response);
  if (!data?.inboxId) {
    const missing: ErrorType = { ErrorCode: response.status, ErrorMessage: 'Respuesta sin inboxId', Errors: [] };
    throw missing;
  }
  return { ...data, inboxId: String(data.inboxId) };
};

/**
 * POST protocols/providers/inbox/{inboxId}/resolve `{ providerId }` o `{ create: { businessName, taxId? } }` → proveedor y
 * archivo. 409 "ya resuelta" → `AlreadyResolved` con `data = { providerId, providerFileId }`; 409 "El CUIT pertenece a
 * una persona que no es proveedor", 400 CUIT inválido, 404, 422 sin tipo CUIT y 502 → ErrorType con el mensaje de la API.
 */
export const ResolveProviderInbox = async (
  token: string,
  inboxId: string,
  body: ProviderInboxResolveRequest
): Promise<ProviderInboxResolveResult> => {
  const context = 'ResolveProviderInbox';
  const response = await send(ResolveURL(inboxId), { method: 'POST', body: JSON.stringify(body), headers: buildHeaders(token) }, context);
  if (!response.ok) {
    const { error, body: text } = await failure(response, context);
    if (response.status === 409) {
      try {
        const parsed = JSON.parse(text);
        const data = parsed?.data ?? parsed?.Data;
        const providerId = Number(data?.providerId ?? data?.ProviderId);
        if (providerId > 0) {
          const fileId = data?.providerFileId ?? data?.ProviderFileId;
          error.AlreadyResolved = { providerId, providerFileId: fileId ? String(fileId) : null };
        }
      } catch {
        // cuerpo sin JSON: queda como 409 genérico
      }
    }
    throw error;
  }
  const data = await dataOf<ProviderInboxResolveResult>(response);
  if (!data?.providerId) {
    const missing: ErrorType = { ErrorCode: response.status, ErrorMessage: 'Respuesta sin providerId', Errors: [] };
    throw missing;
  }
  return data;
};

/** DELETE protocols/providers/inbox/{inboxId} → 204 (idempotente: también si ya estaba resuelta o vencida); 404 se ignora. */
export const DiscardProviderInbox = async (token: string, inboxId: string): Promise<void> => {
  const context = 'DiscardProviderInbox';
  const response = await send(DiscardURL(inboxId), { method: 'DELETE', headers: buildHeaders(token) }, context);
  if (response.ok || response.status === 404) return;
  throw (await failure(response, context)).error;
};
